import type {
  GetFeaturesInnerParams,
  ModalProps,
} from '@/app/components/search/components/Card/Details/Features/getFeatures/types.ts';
import type { Feature } from '@/app/components/search/components/Card/utils/facilitiesTypes.ts';
import { FeatureType } from '@/app/components/search/components/Card/utils/facilitiesTypes.ts';
import {
  getDatesWithMealLabel,
  getDatesWithoutMealLabel,
  getFlexibleDatesUSPMarkup,
} from '@/app/components/search/components/Card/Details/Features/getFeatures/utils/getDateUtils.tsx';
import React from 'react';
import dynamic from 'next/dynamic';
import { dynamicImportCatch } from '@/utils/appUtilsClient.tsx';

export const FlexibleAlternativeDatesModal = dynamic(
  // eslint-disable-next-line import/no-cycle
  () => import('../../../../../Modal/Modals/FlexibleAlternativeDatesModal').catch(dynamicImportCatch),
  {
    ssr: false,
  }
);

/**
 * Creates a modal component for flexible alternative dates if conditions are met
 */
const createFlexibleDatesModal = (
  showModal: boolean,
  dealCard: GetFeaturesInnerParams['dealCard']
): Partial<Feature> => {
  if (!showModal || !dealCard) return {};

  return {
    Modal: ({ isOpen, onClose }: ModalProps) => (
      <FlexibleAlternativeDatesModal isOpen={isOpen} onClose={onClose} dealCard={dealCard} />
    ),
  };
};

/**
 * Returns features for live dates
 */
export const getLiveSearchFeatures = (params: GetFeaturesInnerParams): Feature[] => {
  const { meal } = params;
  const features: Feature[] = [];

  features.push({
    icon: undefined,
    text: meal ? getDatesWithMealLabel(params) : getDatesWithoutMealLabel(params),
    className: 'dates',
    featureType: FeatureType.normal,
  });

  return features;
};

/**
 * Returns features for flexible dates
 */
export const getFlexibleFeatures = (params: GetFeaturesInnerParams): Feature[] => {
  const {
    dealCard,
    datesTextWithDay,
    showFlexibleAlternativeDates,
    isClient,
    isOverlay,
    isHO,
    meal,
    nightsText,
    datesText,
  } = params;

  const showFlexibleAlternativeDatesModal = showFlexibleAlternativeDates && isClient && !isOverlay;
  const features: Feature[] = [];

  const getIcon = () => {
    if (isHO && isOverlay) return undefined;
    if (isHO) return { category: 'Weather and sky', name: 'icon-moon', color: 'iconPrimary' } as const;
    return { category: 'Office and stationery', name: 'icon-calendar-grid', color: 'iconPrimary' } as const;
  };

  const getText = () => getFlexibleDatesUSPMarkup(params, isHO ? `${nightsText} · ${datesText}` : datesTextWithDay);

  if (showFlexibleAlternativeDates) {
    features.push({
      icon: getIcon(),
      text: getText(),
      className: 'dates dates-more',
      featureType: FeatureType.dates,
      ...createFlexibleDatesModal(showFlexibleAlternativeDatesModal, dealCard),
    });
  } else {
    features.push({
      icon: getIcon(),
      text: getText(),
      className: 'dates',
      featureType: FeatureType.dates,
    });
  }

  if (!isHO) {
    features.push({
      icon: undefined,
      text: meal ? getDatesWithMealLabel(params) : getDatesWithoutMealLabel(params),
      className: 'dates',
      featureType: FeatureType.normal,
    });
  }

  return features;
};

/**
 * Returns features for accommodation dates
 */
export const getAccommodationDates = (params: GetFeaturesInnerParams): Feature[] => {
  const { datesTextWithDay } = params;

  const features: Feature[] = [];

  features.push({
    icon: { category: 'Services', name: 'icon-double-bed', color: 'iconPrimary' } as const,
    text: `${datesTextWithDay}`,
    className: 'dates dates-partial-stay',
    featureType: FeatureType.dates,
  });

  return features;
};

/**
 * Returns features for opx flex dates
 */
export const getOpxFlexDates = (params: GetFeaturesInnerParams): Feature[] => {
  const { datesTextWithDay } = params;

  const features: Feature[] = [];

  features.push({
    icon: { category: 'Office and stationery', name: 'icon-calendar-grid', color: 'iconPrimary' } as const,
    text: `${datesTextWithDay}`,
    className: 'dates opx-flex-dates',
    featureType: FeatureType.dates,
  });

  return features;
};
